import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  getCatalog,
  getTaskLayout,
  saveTaskLayout,
  saveTaskPrompt,
  setTaskOverviewEnabled,
  setTaskShowProjectDetail,
  setTaskShowFilesTab,
  setTaskAllowLocalFiles,
} from '../Api/agentWidgetsApi';

export const fetchWidgetCatalog = createAsyncThunk(
  'agentWidgets/fetchCatalog',
  async () => getCatalog(),
);

export const fetchAgentLayout = createAsyncThunk(
  'agentWidgets/fetchLayout',
  async (taskId) => {
    const data = await getTaskLayout(taskId);
    return { taskId, ...data };
  },
);

export const saveAgentLayout = createAsyncThunk(
  'agentWidgets/saveLayout',
  async ({ taskId, layout }) => {
    const data = await saveTaskLayout(taskId, layout);
    return { taskId, ...data };
  },
);

export const updateAgentPrompt = createAsyncThunk(
  'agentWidgets/updatePrompt',
  async ({ taskId, widgetKey, promptText }) => {
    const data = await saveTaskPrompt(taskId, widgetKey, promptText);
    return { taskId, widgetKey, promptText, ...data };
  },
);

export const updateOverviewEnabled = createAsyncThunk(
  'agentWidgets/updateOverviewEnabled',
  async ({ taskId, isEnabled }) => {
    const data = await setTaskOverviewEnabled(taskId, isEnabled);
    return { taskId, isEnabled: data.is_enabled };
  },
);

export const updateShowProjectDetail = createAsyncThunk(
  'agentWidgets/updateShowProjectDetail',
  async ({ taskId, show }) => {
    const data = await setTaskShowProjectDetail(taskId, show);
    return { taskId, show: data.show };
  },
);

export const updateShowFilesTab = createAsyncThunk(
  'agentWidgets/updateShowFilesTab',
  async ({ taskId, show }) => {
    const data = await setTaskShowFilesTab(taskId, show);
    return { taskId, show: data.show };
  },
);

export const updateAllowLocalFiles = createAsyncThunk(
  'agentWidgets/updateAllowLocalFiles',
  async ({ taskId, allow }) => {
    const data = await setTaskAllowLocalFiles(taskId, allow);
    return { taskId, allow: data.allow };
  },
);

const initialState = {
  catalog: [],
  layoutByTask: {},              // { [taskId]: [{ widget_key, position, is_enabled }] }
  promptsByTask: {},             // { [taskId]: { widget_key: prompt_text } }
  overviewEnabledByTask: {},     // { [taskId]: bool } — admin per-agent on/off for Project Overview
  showProjectDetailByTask: {},   // { [taskId]: bool } — admin per-agent: show ProjectDetail or go direct to chat
  showFilesTabByTask: {},        // { [taskId]: bool } — admin per-agent: show Files tab in chat sidebar
  allowLocalFilesByTask: {},     // { [taskId]: bool } — admin per-agent: allow local file uploads
  layoutLoadingByTask: {},       // { [taskId]: bool } — true while fetchAgentLayout is in flight
  dirtyTasks: {},                // { [taskId]: true }
  savingTaskId: null,
  status: 'idle',
  error: null,
};

const slice = createSlice({
  name: 'agentWidgets',
  initialState,
  reducers: {
    reorderLayout: (s, a) => {
      const { taskId, fromIdx, toIdx } = a.payload;
      const layout = s.layoutByTask[taskId] ? [...s.layoutByTask[taskId]] : [];
      const enabledOnly = layout.filter(l => l.is_enabled).sort((x, y) => x.position - y.position);
      const [moved] = enabledOnly.splice(fromIdx, 1);
      enabledOnly.splice(toIdx, 0, moved);
      // rewrite positions
      const repositioned = enabledOnly.map((l, i) => ({ ...l, position: i }));
      const disabled = layout.filter(l => !l.is_enabled);
      s.layoutByTask[taskId] = [...repositioned, ...disabled];
      s.dirtyTasks[taskId] = true;
    },
    toggleWidget: (s, a) => {
      const { taskId, widgetKey } = a.payload;
      const layout = s.layoutByTask[taskId] ? [...s.layoutByTask[taskId]] : [];
      const existing = layout.find(l => l.widget_key === widgetKey);
      if (existing) {
        existing.is_enabled = !existing.is_enabled;
        if (existing.is_enabled) {
          const maxPos = Math.max(-1, ...layout.filter(l => l.is_enabled && l.widget_key !== widgetKey).map(l => l.position));
          existing.position = maxPos + 1;
        }
      } else {
        const maxPos = Math.max(-1, ...layout.filter(l => l.is_enabled).map(l => l.position));
        layout.push({ widget_key: widgetKey, position: maxPos + 1, is_enabled: true });
      }
      s.layoutByTask[taskId] = layout;
      s.dirtyTasks[taskId] = true;
    },
    removeWidget: (s, a) => {
      const { taskId, widgetKey } = a.payload;
      const layout = s.layoutByTask[taskId] ? [...s.layoutByTask[taskId]] : [];
      const existing = layout.find(l => l.widget_key === widgetKey);
      if (existing) existing.is_enabled = false;
      s.layoutByTask[taskId] = layout;
      s.dirtyTasks[taskId] = true;
    },
    clearDirty: (s, a) => {
      s.dirtyTasks[a.payload] = false;
    },
    revertLayout: (s, a) => {
      // Forces a re-fetch; caller should dispatch fetchAgentLayout after.
      delete s.layoutByTask[a.payload];
      s.dirtyTasks[a.payload] = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWidgetCatalog.fulfilled, (s, a) => {
        s.catalog = a.payload.catalog || [];
      })
      .addCase(fetchAgentLayout.pending, (s, a) => {
        s.layoutLoadingByTask[a.meta.arg] = true;
      })
      .addCase(fetchAgentLayout.fulfilled, (s, a) => {
        const { taskId, layout, prompts, is_overview_enabled, show_project_detail, show_files_tab, allow_local_files } = a.payload;
        s.layoutByTask[taskId] = layout || [];
        s.promptsByTask[taskId] = prompts || {};
        s.overviewEnabledByTask[taskId] =
          is_overview_enabled === undefined ? true : !!is_overview_enabled;
        s.showProjectDetailByTask[taskId] =
          show_project_detail === undefined ? true : !!show_project_detail;
        s.showFilesTabByTask[taskId] =
          show_files_tab === undefined ? true : !!show_files_tab;
        s.allowLocalFilesByTask[taskId] =
          allow_local_files === undefined ? true : !!allow_local_files;
        s.dirtyTasks[taskId] = false;
        s.layoutLoadingByTask[taskId] = false;
      })
      .addCase(fetchAgentLayout.rejected, (s, a) => {
        s.layoutLoadingByTask[a.meta.arg] = false;
      })
      .addCase(updateOverviewEnabled.fulfilled, (s, a) => {
        const { taskId, isEnabled } = a.payload;
        s.overviewEnabledByTask[taskId] = !!isEnabled;
      })
      .addCase(updateShowProjectDetail.pending, (s, a) => {
        // Optimistic update — flip immediately so toggle feels instant
        const { taskId, show } = a.meta.arg;
        s.showProjectDetailByTask[taskId] = !!show;
      })
      .addCase(updateShowProjectDetail.fulfilled, (s, a) => {
        const { taskId, show } = a.payload;
        s.showProjectDetailByTask[taskId] = !!show;
      })
      .addCase(updateShowProjectDetail.rejected, (s, a) => {
        const { taskId, show } = a.meta.arg;
        s.showProjectDetailByTask[taskId] = !show;
      })
      .addCase(updateShowFilesTab.pending, (s, a) => {
        const { taskId, show } = a.meta.arg;
        s.showFilesTabByTask[taskId] = !!show;
      })
      .addCase(updateShowFilesTab.fulfilled, (s, a) => {
        const { taskId, show } = a.payload;
        s.showFilesTabByTask[taskId] = !!show;
      })
      .addCase(updateShowFilesTab.rejected, (s, a) => {
        const { taskId, show } = a.meta.arg;
        s.showFilesTabByTask[taskId] = !show;
      })
      .addCase(updateAllowLocalFiles.pending, (s, a) => {
        const { taskId, allow } = a.meta.arg;
        s.allowLocalFilesByTask[taskId] = !!allow;
      })
      .addCase(updateAllowLocalFiles.fulfilled, (s, a) => {
        const { taskId, allow } = a.payload;
        s.allowLocalFilesByTask[taskId] = !!allow;
      })
      .addCase(updateAllowLocalFiles.rejected, (s, a) => {
        const { taskId, allow } = a.meta.arg;
        s.allowLocalFilesByTask[taskId] = !allow;
      })
      .addCase(saveAgentLayout.pending, (s, a) => {
        s.savingTaskId = a.meta.arg.taskId;
      })
      .addCase(saveAgentLayout.fulfilled, (s, a) => {
        const { taskId, layout } = a.payload;
        s.layoutByTask[taskId] = layout || [];
        s.dirtyTasks[taskId] = false;
        s.savingTaskId = null;
      })
      .addCase(saveAgentLayout.rejected, (s) => {
        s.savingTaskId = null;
      })
      .addCase(updateAgentPrompt.fulfilled, (s, a) => {
        const { taskId, widgetKey, promptText } = a.payload;
        if (!s.promptsByTask[taskId]) s.promptsByTask[taskId] = {};
        s.promptsByTask[taskId][widgetKey] = promptText;
      });
  },
});

export const {
  reorderLayout, toggleWidget, removeWidget, clearDirty, revertLayout,
} = slice.actions;
export default slice.reducer;
